import {
  PhonicsLibraryPicker,
  type PickedPhonics,
} from "@/components/lessonGenerator/PhonicsLibraryPicker";
import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import { type HandWritingSlideData, MediaUsageContextSchema } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { DEFAULT_VOICE } from "@ai-teach/utils";
import { useState } from "react";
import { HiX } from "react-icons/hi";
import { EditorContainer } from "../EditorContainer";
import { HandWritingWordAudio } from "./HandWritingWordAudio";

const EMPTY_MEDIA = { src: null, id: null, filename: null };

export function HandWritingEditor() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const [phonicsTarget, setPhonicsTarget] = useState<number | null>(null);
  const [uploadingWord, setUploadingWord] = useState<number | null>(null);
  const [voiceSettingsIndex, setVoiceSettingsIndex] = useState<number | null>(null);

  const slideData = slide?.slideData as HandWritingSlideData | undefined;

  const setWordAudio = (index: number, media: HandWritingSlideData["textAudios"][number]) => {
    if (!slide || !slideData) return;

    const textAudios = slideData.text.map(
      (_, at) => (at === index ? media : slideData.textAudios?.[at]) ?? EMPTY_MEDIA
    );

    dispatch(updateSlide({ slideId: slide.id, slideData: { ...slideData, textAudios } }));
  };

  const audioUpload = useLocalAudioUpload((media, wordIndex) =>
    setWordAudio(Number(wordIndex), media)
  );

  const uploadWordAudio = async (file: File, index: number) => {
    setUploadingWord(index);
    await audioUpload.handleFileDrop(file, String(index));
    setUploadingWord(null);
  };

  if (!slide || !slideData) return null;

  const { text, textAudios } = slideData;
  const slideName = slide.name ?? "";

  const setText = (nextText: string[], nextAudios?: HandWritingSlideData["textAudios"]) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...slide.slideData,
          text: nextText,
          ...(nextAudios ? { textAudios: nextAudios } : {}),
        },
      })
    );
  };

  const removeWord = (index: number) => {
    setText(
      text.filter((_, i) => i !== index),
      (textAudios ?? []).filter((_, i) => i !== index)
    );
  };

  const handlePickPhonics = ({ media }: PickedPhonics) => {
    if (phonicsTarget === null) return;
    setWordAudio(phonicsTarget, media);
    setPhonicsTarget(null);
  };

  return (
    <EditorContainer>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0 px-3 gap-2">
        <h2 className="flex-1 min-w-0 text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
          {slideName || "Hand Writing"}
        </h2>
        <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
          Hand Writing
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3">
        {/* Words the student traces, in the order they are written */}
        <div className="bg-surface rounded-lg border border-border overflow-hidden shrink-0">
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
              Words
            </span>
          </div>

          <div className="p-3 space-y-2">
            {text.length === 0 && (
              <p className="text-xs font-medium text-muted-foreground text-center py-2">
                No words yet
              </p>
            )}

            {text.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: entries are plain strings with no stable id
              <div key={index} className="space-y-1.5 pb-2 border-b border-border last:border-0">
                <div className="flex items-center gap-2">
                  <label
                    className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest w-14 shrink-0 whitespace-nowrap"
                    htmlFor={`text-${index}`}
                  >
                    {`Text ${index + 1}`}
                  </label>
                  <input
                    type="text"
                    name={`text-${index}`}
                    id={`text-${index}`}
                    value={item}
                    onChange={(e) =>
                      setText(text.map((t, i) => (i === index ? e.target.value : t)))
                    }
                    placeholder={`Word ${index + 1}`}
                    className="flex-1 px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                  <IconButton variant="danger" onClick={() => removeWord(index)}>
                    <HiX className="text-xs" />
                  </IconButton>
                </div>

                <div className="pl-16">
                  <HandWritingWordAudio
                    slideId={slide.id}
                    index={index}
                    audio={textAudios?.[index]}
                    isUploading={uploadingWord === index}
                    canGenerate={item.trim() !== ""}
                    onFileDrop={(file) => void uploadWordAudio(file, index)}
                    onGenerate={() => setVoiceSettingsIndex(index)}
                    onBrowsePhonics={() => setPhonicsTarget(index)}
                    onRemove={() => setWordAudio(index, EMPTY_MEDIA)}
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => setText([...text, ""])}
              className="w-full py-1.5 text-xs font-semibold text-blue-500 border border-dashed border-blue-300 rounded hover:bg-blue-50 transition-colors"
            >
              + Add Text
            </button>
          </div>
        </div>
      </div>

      <PhonicsLibraryPicker
        isOpen={phonicsTarget !== null}
        onClose={() => setPhonicsTarget(null)}
        onPick={handlePickPhonics}
      />

      {voiceSettingsIndex !== null && (
        <VoiceSettingsModal
          audioData={textAudios?.[voiceSettingsIndex] ?? EMPTY_MEDIA}
          onClose={() => setVoiceSettingsIndex(null)}
          text={text[voiceSettingsIndex]?.trim() ?? null}
          slideId={slide.id}
          voice={DEFAULT_VOICE}
          usageContext={MediaUsageContextSchema.Values.aiTextToSpeech}
          onSave={({ src, id, filename }) => {
            setWordAudio(voiceSettingsIndex, { src, id, filename });
            setVoiceSettingsIndex(null);
          }}
        />
      )}
    </EditorContainer>
  );
}
